<template>
  <div>
    <VBox :scrollable="true">
      <b-container fluid>
        <b-row>
          <b-col>
            <b-container>
              <b-row>
                <b-col style="height: 110px" md="6" class="pt-4">
                  <label>{{$tr("allEntityForms","All entity forms")}}</label>
                  <b-form-select
                      v-model="selectedForm"
                      :options="listOfForms"
                  ></b-form-select>
                </b-col>
                <b-col md="6" class="pt-4">
                  <label>{{$tr("selectFile","Select file")}}</label>
                  <FileUpload
                      data-test-path="data-import-file-upload"
                      @file-selected="uploadFile"
                      ref="dataImportFileUpload"
                      :read-only="!selectedForm || (showSeason && !selectedSeasonId)"
                  />
                </b-col>
              </b-row>
              <b-row v-if="showSeason">
                <b-col md="6">
                  <b-form-group>
                    <slot name="label">
                      {{$tr("season","Season")}}
                    </slot>
                    <div class="input-group">
                      <div class="form-control grey-label readonly-input">
                        <div v-html="seasonName"></div>
                      </div>
                      <b-button
                          data-test-path="tree-select-button"
                          class="form-button ml-1 d-none d-xl-inline-block"
                          variant="plm-action"
                          @click="selectSeason"
                      >
                        <font-awesome-icon icon="pencil-alt" class="mr-2"/>
                        {{$tr("select","Select")}}
                      </b-button>
                      <b-button
                          class="form-button-icon ml-1 d-xl-none"
                          variant="plm-action"
                          @click="selectSeason"
                      >
                        <font-awesome-icon icon="pencil-alt" class="mr-2"/>
                      </b-button>
                    </div>
                    <small v-if="!selectedSeasonId" class="form-text text-danger"
                           style="margin-top: 2px; margin-left: 10px;">
                      {{$tr("seasonFieldCantByEmpty","Season FIELD can't be empty")}}
                    </small>
                  </b-form-group>
                </b-col>
              </b-row>
              <b-row class="pt-3">
                <b-col cols="auto" v-for="field in selectedFormFields" :key="field.id">
                  <b-form-checkbox>
                    {{ field.fieldLabel }}
                  </b-form-checkbox>
                </b-col>
              </b-row>
              <b-row>
                <b-col class="pt-4" v-if="!allBugs.length  && fileIsReady" data-test-path="file-upload-info">
                  <div v-if="numberOfImportedRecords !== 0">{{$tr("theFileHasBeenLoadedSuccessfully.","The file has been loaded successfully.")}}</div>
                  <div v-if="numberOfImportedRecords !== 0">{{ numberOfImportedRecords }} {{$tr("recordHaveBennImported.","records have been imported.")}}
                  </div>
                </b-col>
                <b-col class="pt-4" v-if="allBugs.length">
                  {{$tr("errors","Errors")}}:
                  <b-container>
                    <b-row v-for="bug of allBugs" :key="bug.id">
                      {{ bug }}
                    </b-row>
                  </b-container>
                </b-col>
                <b-col class="pt-4" v-else-if="info.length">
                  {{$tr("warnings","Warnings")}}:
                  <b-container>
                    <b-row v-for="information of info" :key="information.id">
                      {{ information }}
                    </b-row>
                  </b-container>
                </b-col>
              </b-row>
            </b-container>
          </b-col>
        </b-row>
      </b-container>
    </VBox>
    <FooterNavbar data-test-path="data-import-footer">
      <CloseButton @close="close"></CloseButton>
    </FooterNavbar>
  </div>
</template>

<script>

import Activity from "@/activity/Activity";
import FooterNavbar from "@/components/navbar/FooterNavbar";
import CloseButton from "@/components/navbar/CloseButton";
import VBox from "@/components/VBox";
import FileUpload from "@/components/FileUpload";
import RpcInputValidationException from "@/server/com/silkplm/engine/rpc/exceptions/RpcInputValidationException";
import SaveAndCloseButton from "@/components/navbar/SaveAndCloseButton";
import DialogManager from "@/utils/DialogManager";
import Category from "@/server/com/silkplm/model/plm/Category";
import Product from "@/server/com/silkplm/model/plm/Product";
import FormsLoader from "@/components/forms/FormsLoader";
import Suppliers from "@/server/com/silkplm/model/plm/Suppliers";
import Perms from "@/server/Perms";
import TermsOfPayment from "@/server/com/silkplm/model/plm/dictionaries/TermsOfPayment";
import CBMCostToSeason from "@/server/com/silkplm/model/plm/dictionaries/CBMCostToSeason";
import TransitTime from "@/server/com/silkplm/model/plm/dictionaries/TransitTime";
import FormsActions from "@/components/forms/FormsActions";
import Season from "@/server/com/silkplm/model/plm/Season";
import moment from "moment-timezone";
import BudgetingPlannerPM from "@/server/com/silkplm/model/plm/BudgetingPlannerPM";
import Size from "@/server/com/silkplm/model/plm/dictionaries/Size";
import {
  checkIfEnabledByOrganizationFormsSettingsForForm
} from "@/components/forms/customizations/plm/EntityEditFormService";
import SizeRanges from "@/server/com/silkplm/model/plm/dictionaries/SizeRanges";
import SizesPercentageBreakdown from "@/server/com/silkplm/model/plm/dictionaries/SizesPercentageBreakdown";
import SizeCoding from "@/server/com/silkplm/model/plm/dictionaries/SizeCoding";
import ComponentIndex from "@/server/com/silkplm/model/plm/dictionaries/ComponentIndex";


const FORM_TYPE_PRODUCT = Product;
const FORM_TYPE_CATEGORY = Category;
const FORM_TYPE_SUPPLIERS = Suppliers;
const FORM_TYPE_TERMS_OF_PAYMENT = TermsOfPayment;
const FORM_TYPE_SIZE = Size;
const FORM_TYPE_CBM_COST_TO_SEASON = CBMCostToSeason;
const FORM_TYPE_TRANSIT_TYPE = TransitTime;
const FORM_TYPE_BUDGETING_PLANNER_PM = BudgetingPlannerPM;
const FORM_TYPE_SIZE_RANGE = SizeRanges;
const FORM_TYPE_SIZE_PERCENTAGE_BREAKDOWN = SizesPercentageBreakdown;
const FORM_TYPE_SIZE_CODING = SizeCoding;
const FORM_TYPE_COMPONENT_INDEX = ComponentIndex;

moment.suppressDeprecationWarnings = true;

export default {
  name: "DataImport",
  components: {SaveAndCloseButton, FileUpload, VBox, CloseButton, FooterNavbar},
  permission: Perms.SUPERUSER,

  async mounted() {
    for (let form of FormsLoader.forms) {
      switch (form.formClassLong) {
        case this.formConfigProduct.formClassLong:
        case this.formConfigCategory.formClassLong:
        case this.formConfigSuppliers.formClassLong:
        case this.formConfigTermsOfPayment.formClassLong:
        case this.formConfigCbmCostToSeason.formClassLong:
        case this.formConfigTransitTime.formClassLong:
        case this.formConfigSizeRange.formClassLong:
        case this.formConfigSizePercentageBreakdown.formClassLong:
        case this.formComponentIndex.formClassLong:
        case this.formConfigBudgetingPlannerPM.formClassLong: {
          this.listOfForms.push({value: form, text: form.formTitle});
          break;
        }
        case this.formConfigSize.formClassLong: {
          if (SessionManager.featureFlags?.length) {
            let enabledByConfiguration = checkIfEnabledByOrganizationFormsSettingsForForm(this.formConfigSize.formId);
            if (enabledByConfiguration) {
              this.listOfForms.push({value: form, text: form.formTitle});
              break;
            }
          }
          break;
        }
        case this.formConfigSizeCoding.formClassLong: {
          if (SessionManager.featureFlags?.length) {
            let enabledByConfiguration = checkIfEnabledByOrganizationFormsSettingsForForm(this.formConfigSizeCoding.formId);
            if (enabledByConfiguration) {
              this.listOfForms.push({value: form, text: form.formTitle});
              break;
            }
          }
        }
      }
    }
    this.listOfForms.sort((a, b) => (a.text > b.text) ? 1 : -1);
  },

  methods: {

    ...Activity.methods,

    getTitle() {
      return $tr('manualDataImport','Manual Data Import');
    },

    async uploadFile() {
      let fileUpload = this.$refs.dataImportFileUpload;
      this.file = fileUpload.getFile();
      this.loading = true;
      fileUpload.setProgress(0);
      try {
        await Rpc.BusinessEntities.uploadAttachment(this.file).lock().onProgress((e) => {
          fileUpload.setProgress(e.percent);
          this.percentDone = Math.floor(e.percent);
        });
        this.loading = false;
        this.percentDone = 0;
        fileUpload.reset();
      } catch (e) {
        this.loading = false;
        this.percentDone = 0;
        if (e instanceof RpcInputValidationException) {
          fileUpload.setError(e.fields.$field.errorMessage);
          this.allBugs = 0;
          this.fileIsReady = false;
        }
      }
      let res = await Rpc.plm.DataImport.importData(this.file, this.selectedForm.formClassLong, this.selectedSeasonId).lock();
      this.info = res.info;
      this.allBugs = res.bugs;
      this.numberOfImportedRecords = res.numberOfImportedRecords;
      this.fileIsReady = true;
      if (this.allBugs?.length) {
        await DialogManager.info($tr('fileImportError','File import error'),
            $tr('DataImport.file.cannot.imported','The file cannot be imported because it contains errors.') );
      }
    },

    async selectSeason() {
      this.listSeasons = await Rpc.plm.services.BudgetingPlannerService.listSeasons().lock();
      let result = await (await FormsActions.showSelectList(Season)).getResult();
      if (result) {
        this.selectedSeasonId = result.id;
        let selectedSeasonName = result.season;
        if (this.selectedSeasonId) {
          this.seasonName = selectedSeasonName;
        }
      }
    },

  },

  watch: {
    'selectedForm': function (form) {
      switch (form?.formTitle) {
        case this.formConfigTransitTime.formTitle:
        case this.formConfigCbmCostToSeason.formTitle:
        case this.formConfigSizePercentageBreakdown.formTitle:
        case this.formConfigBudgetingPlannerPM.formTitle:
          this.showSeason = true;
          break;
        default:
          this.selectedSeasonId = null;
          this.showSeason = false;
      }
    }
  },

  data: () => ({
    selectedForm: null,
    selectedFormFields: null,
    listOfForms: [],
    file: null,
    allBugs: [],
    info: [],
    numberOfImportedRecords: 0,
    fileIsReady: false,
    loading: false,
    percentDone: 0,
    formConfigProduct: FormsLoader.detectFormConfig(FORM_TYPE_PRODUCT),
    formConfigCategory: FormsLoader.detectFormConfig(FORM_TYPE_CATEGORY),
    formConfigSuppliers: FormsLoader.detectFormConfig(FORM_TYPE_SUPPLIERS),
    formConfigTermsOfPayment: FormsLoader.detectFormConfig(FORM_TYPE_TERMS_OF_PAYMENT),
    formConfigSize: FormsLoader.detectFormConfig(FORM_TYPE_SIZE),
    formConfigCbmCostToSeason: FormsLoader.detectFormConfig(FORM_TYPE_CBM_COST_TO_SEASON),
    formConfigTransitTime: FormsLoader.detectFormConfig(FORM_TYPE_TRANSIT_TYPE),
    formConfigBudgetingPlannerPM: FormsLoader.detectFormConfig(FORM_TYPE_BUDGETING_PLANNER_PM),
    formConfigSizeRange: FormsLoader.detectFormConfig(FORM_TYPE_SIZE_RANGE),
    formConfigSizePercentageBreakdown: FormsLoader.detectFormConfig(FORM_TYPE_SIZE_PERCENTAGE_BREAKDOWN),
    formConfigSizeCoding: FormsLoader.detectFormConfig(FORM_TYPE_SIZE_CODING),
    formComponentIndex: FormsLoader.detectFormConfig(FORM_TYPE_COMPONENT_INDEX),
    seasonName: null,
    selectedSeasonId: null,
    showSeason: false,
  }),

}

</script>

<style scoped>

</style>